import { FC, useContext, useEffect, useState } from 'react';
import ScrollAnimation from 'react-animate-on-scroll';
import styled, { css, keyframes } from 'styled-components';

import ClickableSecondaryButton from '@/components/Buttons/ClickableSecondaryButton';
import SecondaryButton from '@/components/Buttons/SecondaryButton';
import ContainerSmall from '@/components/ContainerSmall/ContainerSmall';
import { parseHTML } from '@/lib/parse';
import { translate } from '@/lib/translate';
import { GlobalContext } from '@/pages/_app';

const FooterBanner: FC<any> = ({
	cta,
	pad,
	blog,
	image,
	title,
	chatbot,
	privacy,
	homepage,
	successStories,
	viberForBusiness,
	messagingSolutions,
	marketingSolutions,
	handleButtonClick,
}) => {
	const { lang } = useContext(GlobalContext);
	const [buttonCopy, setButtonCopy] = useState('');

	useEffect(() => {
		const button = document.querySelector('.ot-sdk-show-settings');

		if (!button) return;

		const observer = new MutationObserver((mutationRecords) => {
			if (mutationRecords) {
				setButtonCopy(
					translate(lang, {
						en: 'Cookie Settings',
						ru: 'Настройки cookies',
						ua: 'Налаштування cookies',
					}),
				);
			}
		});

		observer.observe(button, {
			childList: true,
			characterDataOldValue: true,
		});

		return () => observer.disconnect();
	}, []);

	return (
		<Wrapper $successStories={successStories} $lang={lang}>
			<Background />

			{buttonCopy !== '' && (
				<button className="ot-sdk-show-settings cookie_setting_button">
					{buttonCopy}
				</button>
			)}

			<ContainerSmall
				privacy={privacy}
				successStories={successStories}
				viberForBusiness={viberForBusiness}
				marketingSolutions={marketingSolutions}
				messagingSolutions={messagingSolutions}
			>
				<Content
					$pad={pad}
					$blog={blog}
					$chatbot={chatbot}
					$privacy={privacy}
					$homepage={homepage}
					$marketingSolutions={marketingSolutions}
					$messagingSolutions={messagingSolutions}
					$successStories={successStories}
				>
					<ScrollAnimation animateOnce animateIn="fadeInUp" offset={100}>
						<Title
							$successStories={successStories}
							$viberForBusiness={viberForBusiness}
							$messagingSolutions={messagingSolutions}
						>
							{parseHTML(title)}
						</Title>

						{handleButtonClick ? (
							<ClickableSecondaryButton
								text={cta?.text}
								onClick={handleButtonClick}
							/>
						) : (
							<SecondaryButton href={cta?.link} text={cta?.text} />
						)}
					</ScrollAnimation>

					<ScrollAnimation animateOnce animateIn="fadeIn" offset={0}>
						<picture>
							<Image
								loading="lazy"
								$chatbot={chatbot}
								alt={image.alt}
								src={image.link}
							/>
						</picture>
					</ScrollAnimation>
				</Content>
			</ContainerSmall>
		</Wrapper>
	);
};

const fadeInUpImage = keyframes`
  0% {
    transform: translateY(150px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
`;

const Background = styled.div`
	position: absolute;
	z-index: 200;
	height: 100%;
	width: 100%;
	overflow: hidden;

	&:after {
		content: '';
		position: absolute;
		width: 100%;
		height: calc(100% + 300px);
		top: 0;
		left: 0;
		background-color: var(--color-purple);
		mask-image: paint(smooth-corners);
		--smooth-corners: 12.74, 13;
		z-index: 200;

		@media (max-width: 460px) {
			--smooth-corners: 16.74, 17;
			mask-image: paint(smooth-corners);
		}
	}
`;

const Wrapper = styled.div<{ $successStories?: boolean; $lang: string }>`
	position: relative;
	z-index: 200;

	.cookie_setting_button {
		position: absolute;
		left: 0;
		bottom: 0;

		${({ $lang }) =>
			$lang === 'en' || $lang === 'ru'
				? css`
						width: 140px;
					`
				: css`
						width: 160px;
					`}

		z-index: 301;
		cursor: pointer;
		font-family: var(--font-sans);
		font-weight: var(--font-bold);
		text-transform: uppercase;
		background: white;
		color: var(--color-purple);
		border-top-right-radius: 15px;
		height: auto;
		white-space: normal;
		word-wrap: break-word;
		padding: 0.8em 2em;
		font-size: 0.8em;
		line-height: 1.2;

		@media (max-width: 500px) {
			left: auto;
			right: 0;
			border-top-left-radius: 15px;
			border-top-right-radius: 0;
		}

		&:hover {
			background: linear-gradient(180deg, var(--color-white) 0%, #eae0ff 100%);
		}
	}

	@media (max-width: 800px) {
		overflow: hidden;

		${(successStories) =>
			successStories &&
			css`
				overflow: visible;
			`}
	}
`;

const Content = styled.div<{
	$pad?: boolean;
	$blog?: boolean;
	$chatbot?: boolean;
	$privacy?: boolean;
	$homepage?: boolean;
	$successStories?: boolean;
	$viberForBusiness?: boolean;
	$messagingSolutions?: boolean;
	$marketingSolutions?: boolean;
}>`
	width: 100%;
	padding: 69px 15px 85px;
	position: relative;
	z-index: 300;
	display: flex;
	justify-content: space-between;

	${({ $pad }) =>
		$pad
			? css`
					padding-left: 35px;
				`
			: css`
					padding-left: 0;
				`}

	.fadeInUp {
		animation: ${fadeInUpImage} 1 0.6s forwards;
	}

	${({ $chatbot }) =>
		$chatbot &&
		css`
			padding: 55px 10px 55px;
		`}

	@media (max-width: 500px) {
		padding-top: 31px;
		padding-bottom: 80px;
		justify-content: center;

		${({ $homepage }) =>
			$homepage &&
			css`
				justify-content: start;
			`}

		${({ $marketingSolutions }) =>
			$marketingSolutions &&
			css`
				justify-content: start;
			`}
  
 		 ${({ $messagingSolutions }) =>
			$messagingSolutions &&
			css`
				justify-content: flex-start;
			`}

		${({ $successStories }) =>
			$successStories &&
			css`
				justify-content: flex-start;
			`}
  
 		 ${({ $blog }) =>
			$blog &&
			css`
				justify-content: flex-start;
			`}
  
 		 ${({ $privacy }) =>
			$privacy &&
			css`
				justify-content: flex-start;
			`}
	}
`;

const Title = styled.h2<{
	$successStories?: boolean;
	$viberForBusiness?: boolean;
	$messagingSolutions?: boolean;
}>`
	font-family: var(--font-sans);
	font-weight: var(--font-bold);
	font-size: 41px;
	line-height: 48px;
	color: var(--color-white);
	margin-bottom: 29px;
	width: 300px;
	max-width: 100%;

	@media (max-width: 700px) {
		width: 100%;
	}

	@media (max-width: 700px) {
		width: 100%;
	}

	${({ $successStories }) =>
		$successStories &&
		css`
			width: 300px;
			max-width: 100%;
			@media (max-width: 700px) {
				width: 100%;
			}
		`}

	${({ $viberForBusiness }) =>
		$viberForBusiness &&
		css`
			width: 500px;
			max-width: 100%;
			@media (max-width: 850px) {
				width: 400px;
				max-width: 100%;
			}
			@media (max-width: 700px) {
				width: 100%;
			}
		`}

  ${({ $messagingSolutions }) =>
		$messagingSolutions &&
		css`
			width: 500px;
			max-width: 100%;

			@media (max-width: 850px) {
				width: 400px;
				max-width: 100%;
			}

			@media (max-width: 700px) {
				width: 100%;
			}
		`}
`;

const Image = styled.img<{ $chatbot?: boolean }>`
	width: 500px;
	display: block;
	position: absolute;
	bottom: 0;
	right: 0;

	@media (max-width: 800px) {
		width: 450px;
	}

	@media (max-width: 700px) {
		display: none;
	}

	${({ $chatbot }) =>
		$chatbot &&
		css`
			width: 280px;

			@media (max-width: 800px) {
				width: 280px;
			}
		`}
`;

export default FooterBanner;
